import { createContext, useState, useContext, ReactNode, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import React from 'react';
import {
  identifyBackofficeUser,
  trackBackofficeLogin,
  trackBackofficeLogout,
} from '../analytics';
import { apiService } from '../services/apiService';
import { configService } from '../services/configService';
import { ENV_CONFIG } from '../config/environment';
import { isValidOnboardingTimestamp } from '../utils/onboardingStorage';
import { clearMaintenanceBypass } from '../utils/maintenanceBypass';
import { redirectToLogin, SESSION_INVALID_EVENT } from '../services/sessionNavigation';
import { applyUserPreferredLanguage, maybeApplyMarketLanguage } from '../utils/applyUserPreferredLanguage';
import { shouldRedirectToLoginOnSessionExpiry } from '../utils/publicSessionRoutes';
import { applyProStripeAccountIdToUser } from '../utils/stripeConnectStatus';

/** Fenêtre pour réhydrater l’UI depuis localStorage au démarrage (JWT web court, JWT natif long). */
const CACHED_USER_BOOTSTRAP_WEB_MS = 2 * 60 * 60 * 1000;
const CACHED_USER_BOOTSTRAP_NATIVE_MS = 30 * 24 * 60 * 60 * 1000;


/**
 * Définition de l'interface User.
 * On ajoute la propriété stripe_account_id et profile_complete pour vérifier la configuration.
 */


export interface User {
  user_id: number;
  name: string;
  email: string;
  phone?: string;
  profile_photo_url?: string;
  pro_id?: number; // facultatif, si on n'est pas forcément éducateur
  is_premium?: boolean;
  stripe_customer_id?: string; // facultatif, selon ton usage
  stripe_account_id?: string;  // Ajouté pour savoir si le compte Stripe est configuré
  profile_complete?: boolean;  // Pour savoir si le profil est complet
  is_beta_tester?: boolean;    // Pour identifier les utilisateurs beta testeurs
  /** Présent si l'utilisateur est dans la table `admin` (hydraté via get-profile). */
  is_admin?: boolean;
  association_id?: number;     // ID de l'association si le pro en fait partie
  is_boarding_enabled?: boolean; // Active l'onglet Pension
  /** Pays d'activité du pro (FR | BE | NL), hydraté via get-pro. */
  country?: string | null;
  /** Langue des documents. Vide ou fr sur un compte NL = néerlandais par défaut. */
  invoice_language?: string | null;
  business_registration_type?: string | null;
  business_registration_id?: string | null;
  siret?: string | null;
  google_id?: string;          // ID Google si le compte est lié
  onboarding_completed_at?: string | null; // Si défini, le pro a validé le démarrage (onboarding terminé)
  laika_data_access_enabled?: boolean;
  laika_data_access_accepted_at?: string | null;
  laika_data_access_consent_version?: string | null;
  /** True si l'utilisateur a déjà eu un abonnement (Stripe ou Apple) — pas de nouvelle période d'essai au checkout */
  has_subscription_history?: boolean;
  /** Signature électronique de documents (flag pro `document_esign`, hydraté via get-pro). */
  document_esign_enabled?: boolean;
  account_restriction?: {
    is_limited: boolean;
    reason?: string;
    message?: string;
    support_email?: string;
  };
  preferred_language?: string;
}

/**
 * Types pour le contexte.
 */
interface AuthContextType {
  user: User | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
  isAuthenticated: boolean;
  isLoading: boolean;
  isBetaTester: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Si la base ne marque pas l'onboarding terminé, enlever un startup_dismissed fantôme (ex. autre compte / ancien état). */
function clearStartupDismissedIfOnboardingOpen(proId: number | undefined, onboardingAt: unknown) {
  if (!proId) return;
  if (isValidOnboardingTimestamp(onboardingAt)) return;
  try {
    localStorage.removeItem(`onboarding:v1:user:${proId}:startup_dismissed`);
  } catch {
    /* ignore */
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isBetaTester, setIsBetaTester] = useState<boolean>(false);



  // Tente de remplacer la photo de profil utilisateur par la photo du Trainer (pro) si disponible
  const enrichWithProPhoto = useCallback(async (inputUser: User): Promise<User> => {
    try {
      if (!inputUser?.pro_id) return inputUser;
      const token = localStorage.getItem('token');
      if (!token) return inputUser;
      const response = await apiService.get<any>(`/get-pro/${inputUser.pro_id}`);
      const pro = response.data?.pro || response.data || {};
      const proProfilePhoto: string | undefined = pro.profile_photo_url || pro.user_profile_photo_url;
      const enriched: User = applyProStripeAccountIdToUser({ ...inputUser }, pro);
      if (proProfilePhoto) {
        enriched.profile_photo_url = proProfilePhoto;
      }
      // Temporairement désactivé pour masquer les accès aux Pensions
      // if (typeof pro.is_boarding_enabled !== 'undefined') {
      //   enriched.is_boarding_enabled = !!pro.is_boarding_enabled;
      // }
      // enriched.is_boarding_enabled = false; // Temporairement désactivé

      // Réactivation du module Pension
      if (typeof pro.is_boarding_enabled !== 'undefined') {
        enriched.is_boarding_enabled = !!pro.is_boarding_enabled;
      }
      if (pro.country) {
        enriched.country = String(pro.country).toUpperCase().slice(0, 2);
      }
      if (typeof pro.invoice_language !== 'undefined') {
        enriched.invoice_language = pro.invoice_language ?? null;
      }
      if (typeof pro.business_registration_type !== 'undefined') {
        enriched.business_registration_type = pro.business_registration_type ?? null;
      }
      if (typeof pro.business_registration_id !== 'undefined') {
        enriched.business_registration_id = pro.business_registration_id ?? null;
      }
      if (typeof pro.siret !== 'undefined') {
        enriched.siret = pro.siret ?? null;
      }
      if (typeof pro.laika_data_access_enabled !== 'undefined') {
        enriched.laika_data_access_enabled = !!pro.laika_data_access_enabled;
        enriched.laika_data_access_accepted_at = pro.laika_data_access_accepted_at ?? null;
        enriched.laika_data_access_consent_version = pro.laika_data_access_consent_version ?? null;
      }
      if (typeof pro.document_esign_enabled !== 'undefined') {
        enriched.document_esign_enabled = !!pro.document_esign_enabled;
      }
      // onboarding_completed_at vient de GET /get-profile (user), pas de get-pro — évite la double source
      return enriched;
    } catch (_) {
      return inputUser;
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    const response = await apiService.get<User>('/get-profile', { timeout: 10000 });
    const refreshed = { ...response.data } as User;
    const storedProId = localStorage.getItem('pro_id');
    if (storedProId && (refreshed.pro_id == null || refreshed.pro_id === undefined)) {
      refreshed.pro_id = parseInt(storedProId, 10);
    }
    clearStartupDismissedIfOnboardingOpen(refreshed.pro_id, refreshed.onboarding_completed_at);

    const enriched = refreshed.pro_id ? await enrichWithProPhoto(refreshed) : refreshed;
    // Ne pas réappliquer preferred_language ici : un get-profile en vol
    // (DEFAULT DB = fr) écrase un changement de langue tout juste fait.
    setUser(enriched);
    setIsAuthenticated(true);
    setIsBetaTester(!!enriched.is_beta_tester);
    identifyBackofficeUser(enriched);
    try {
      localStorage.setItem('lastLoginTime', Date.now().toString());
    } catch {
      /* ignore */
    }
    localStorage.setItem('userData', JSON.stringify(enriched));
    return enriched;
  }, [enrichWithProPhoto]);

  // Fonction utilitaire pour gérer les erreurs d'authentification
  const handleAuthError = (error: any) => {
    console.log('handleAuthError appelé avec:', {
      error: error.message,
      status: error.response?.status,
      url: error.config?.url
    });

    const lastLoginTime = localStorage.getItem('lastLoginTime');
    const now = Date.now();
    const timeSinceLogin = lastLoginTime ? now - parseInt(lastLoginTime) : 0;

    const softRecoveryMs = configService.isNative() ? 2 * 60 * 60 * 1000 : 1800000;

    // Si la session est encore « fraîche », ne pas déconnecter immédiatement
    if (timeSinceLogin < softRecoveryMs) {
      console.warn('Erreur d\'authentification temporaire, tentative de récupération...');

      // Tentative de refresh du profil après un court délai
      setTimeout(() => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          apiService.get<any>('/get-profile', { timeout: 5000 })
            .then(async (response) => {
              console.log('Récupération réussie après erreur temporaire');
              const u = response.data as User;
              const storedProIdRecovery = localStorage.getItem('pro_id');
              if (storedProIdRecovery && (u.pro_id == null || u.pro_id === undefined)) {
                u.pro_id = parseInt(storedProIdRecovery, 10);
              }
              clearStartupDismissedIfOnboardingOpen(u.pro_id, u.onboarding_completed_at);
              const restored = u.pro_id ? await enrichWithProPhoto(u) : u;
              setUser(restored);
              setIsAuthenticated(true);
              identifyBackofficeUser(restored);
              try {
                localStorage.setItem('userData', JSON.stringify(restored));
              } catch { /* */ }
            })
            .catch(() => {
              console.warn('Échec de la récupération, mais on garde la session active');
              // Ne pas déconnecter automatiquement
            });
        }
      }, 2000);

      return false; // Ne pas déconnecter immédiatement
    }

    // Si la session est ancienne, déconnexion (token JWT expiré côté API en général)
    console.warn('Session expirée ou erreur d\'authentification prolongée, déconnexion...');
    logout();
    return true; // Déconnexion effectuée
  };

  const login = (token: string, user: User) => {
    if (token && user) {
      localStorage.setItem('token', token);
      localStorage.setItem('lastLoginTime', Date.now().toString()); // Enregistrer le temps de connexion
      localStorage.setItem('userData', JSON.stringify(user)); // Sauvegarder les données utilisateur
      if (user.pro_id) {
        localStorage.setItem('pro_id', String(user.pro_id));
      }
      setUser(user);
      setIsAuthenticated(true);
      setIsBetaTester(!!user.is_beta_tester);
      applyUserPreferredLanguage(user.preferred_language, { force: true });
      trackBackofficeLogin(user);

      // Profil complet (incl. onboarding_completed_at depuis la base) + photo pro
      if (user.pro_id) {
        apiService
          .get<User>('/get-profile', { timeout: 10000 })
          .then((res) => {
            const full = { ...res.data } as User;
            const storedProId = localStorage.getItem('pro_id');
            if (storedProId && (full.pro_id == null || full.pro_id === undefined)) {
              full.pro_id = parseInt(storedProId, 10);
            }
            clearStartupDismissedIfOnboardingOpen(full.pro_id, full.onboarding_completed_at);
            return enrichWithProPhoto(full);
          })
          .then((updated) => {
            // Langue déjà ancrée au login ; get-profile async peut encore
            // renvoyer DEFAULT fr et resetter l'UI si on réapplique ici.
            // Pays BE/NL : nl/de du navigateur si aucun choix explicite.
            maybeApplyMarketLanguage(updated.country, updated.preferred_language);
            setUser(updated);
            identifyBackofficeUser(updated);
            localStorage.setItem('userData', JSON.stringify(updated));
          })
          .catch(() => {
            enrichWithProPhoto(user).then((updated) => {
              setUser(updated);
              identifyBackofficeUser(updated);
              localStorage.setItem('userData', JSON.stringify(updated));
            }).catch(() => {});
          });
      }

      // NOUVEAU: Auto-sync parfaite automatique à chaque connexion
      if (user.user_id) {
        // Déclencher l'auto-sync en arrière-plan (ne pas attendre)
        // Utilise axios directement (pas apiService) pour éviter l'intercepteur de déconnexion
        setTimeout(async () => {
          try {
            const { default: axios } = await import('axios');
            const token = localStorage.getItem('token');
            await axios.post(
              configService.getFullApiUrl(
                `/calendars/user/${user.user_id}/auto-sync-on-login`
              ),
              { login_type: 'normal' },
              { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 15000 }
            );
          } catch (error) {
            // Erreur silencieuse - ne pas perturber l'expérience utilisateur
            console.log('Auto-sync en arrière-plan (non-critique):', error);
          }
        }, 3000); // 3 secondes après la connexion pour laisser l'interface se charger
      }

      // Push : ne jamais afficher la permission système automatiquement.
      // Si l'utilisateur a déjà autorisé les notifications, on synchronise simplement le token.
      // La première demande est déclenchée depuis un CTA explicite dans l'interface.
      if (user.pro_id) {
        setTimeout(async () => {
          try {
            const { syncPushTokenIfAlreadyEnabled, isPushSupported } = await import('../services/pushNotificationService');

            if (!isPushSupported()) return;

            await syncPushTokenIfAlreadyEnabled();
          } catch (error) {
            console.log('Push sync (non-critique):', error);
          }
        }, 2000);
      }
    }
    setIsLoading(false);
  };

  const logout = () => {
    console.log('AuthContext: logout() appelé');
    trackBackofficeLogout();

    const savedAuthToken = localStorage.getItem('token');
    const savedFcmToken = localStorage.getItem('fcm_token');

    // Désinscrire le push avant de supprimer le JWT (valeurs capturées)
    import('../services/pushNotificationService').then(async ({ unregisterPushTokenOnLogout, cleanupPushNotifications }) => {
      await unregisterPushTokenOnLogout(savedFcmToken, savedAuthToken);
      await cleanupPushNotifications();
    }).catch(() => { });

    void import('../services/appBadgeService')
      .then(({ clearAppBadgeCount }) => clearAppBadgeCount())
      .catch(() => {});

    localStorage.removeItem('token');
    localStorage.removeItem('pro_id');
    localStorage.removeItem('lastLoginTime');
    localStorage.removeItem('userData');
    localStorage.removeItem('push_permission_denied');
    localStorage.removeItem('push_permission_asked');
    localStorage.removeItem('push_prompt_dismissed_until');
    clearMaintenanceBypass();

    setUser(null);
    setIsAuthenticated(false);
    setIsBetaTester(false);
    setIsLoading(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('AuthContext: Aucun token trouvé');
      setIsLoading(false);
      return undefined;
    }

    console.log('AuthContext: Token trouvé, démarrage de la vérification...');
    setIsLoading(true);

    const lastLoginTime = localStorage.getItem('lastLoginTime');
    const now = Date.now();
    const timeSinceLogin = lastLoginTime ? now - parseInt(lastLoginTime, 10) : 0;

    const maxBootstrapAge = configService.isNative()
      ? CACHED_USER_BOOTSTRAP_NATIVE_MS
      : CACHED_USER_BOOTSTRAP_WEB_MS;

    let cacheRestored = false;

    if (timeSinceLogin < maxBootstrapAge) {
      const savedUserData = localStorage.getItem('userData');
      if (savedUserData) {
        try {
          const userData = JSON.parse(savedUserData) as User;

          if (!userData.user_id || !userData.email) {
            console.warn('AuthContext: Données utilisateur incomplètes, pas de restauration');
            localStorage.removeItem('userData');
          } else {
            const storedProId = localStorage.getItem('pro_id');
            if (storedProId && (userData.pro_id == null || userData.pro_id === undefined)) {
              userData.pro_id = parseInt(storedProId, 10);
            }
            setUser(userData);
            setIsAuthenticated(true);
            setIsBetaTester(!!userData.is_beta_tester);
            setIsLoading(false);
            cacheRestored = true;
            console.log('AuthContext: Utilisateur restauré depuis localStorage (cache bootstrap)');
          }
        } catch (error) {
          console.warn('AuthContext: Erreur lors du parsing des données utilisateur:', error);
          localStorage.removeItem('userData');
        }
      }
    } else {
      console.log('AuthContext: Cache localStorage ignoré (session inactive depuis longtemps)');
    }

    const schedulePushSync = (proId: number | undefined) => {
      if (!proId) return;
      setTimeout(async () => {
        try {
          const { syncPushTokenIfAlreadyEnabled, isPushSupported } = await import('../services/pushNotificationService');
          if (!isPushSupported()) return;
          await syncPushTokenIfAlreadyEnabled();
        } catch (error) {
          console.log('Push sync (non-critique):', error);
        }
      }, 1000);
    };

    const applyProfileFromApi = (userFromProfile: User) => {
      const storedProId = localStorage.getItem('pro_id');
      if (storedProId && (userFromProfile.pro_id == null || userFromProfile.pro_id === undefined)) {
        userFromProfile.pro_id = parseInt(storedProId, 10);
      }

      clearStartupDismissedIfOnboardingOpen(
        userFromProfile.pro_id,
        userFromProfile.onboarding_completed_at
      );

      try {
        localStorage.setItem('lastLoginTime', Date.now().toString());
      } catch {
        /* ignore */
      }

      if (typeof userFromProfile.is_beta_tester === 'undefined') {
        userFromProfile.is_beta_tester = false;
      }

      localStorage.setItem('userData', JSON.stringify(userFromProfile));

      enrichWithProPhoto(userFromProfile)
        .then((enhanced) => {
          setUser(enhanced);
          setIsAuthenticated(true);
          setIsBetaTester(!!enhanced.is_beta_tester);
          identifyBackofficeUser(enhanced);
          localStorage.setItem('userData', JSON.stringify(enhanced));
          schedulePushSync(enhanced.pro_id);
        })
        .catch(() => {
          setUser(userFromProfile);
          setIsAuthenticated(true);
          setIsBetaTester(!!userFromProfile.is_beta_tester);
          identifyBackofficeUser(userFromProfile);
          schedulePushSync(userFromProfile.pro_id);
        });
    };

    const runProfileRefresh = () =>
      apiService.get<User>('/get-profile', { timeout: 10000 })
        .then((response) => {
          applyProfileFromApi({ ...response.data } as User);
        })
        .catch((error) => {
          console.warn('AuthContext: Erreur lors de la récupération du profil:', error.message);
          console.warn('AuthContext: Détails de l\'erreur:', {
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method
          });

          if (error.response?.status === 401 || error.response?.status === 403) {
            console.warn('AuthContext: Appel de handleAuthError...');
            handleAuthError(error);
          } else {
            console.warn('AuthContext: Erreur non-critique, session maintenue avec données locales');
          }
        });

    if (cacheRestored) {
      void runProfileRefresh();
      return undefined;
    }

    const timer = setTimeout(() => {
      void runProfileRefresh().finally(() => {
        setIsLoading(false);
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [enrichWithProPhoto]);

  // Hors session (pas de JWT) : retirer les attributs <html> du mode nuit (login sans header).
  // Ne pas toucher à localStorage — la préférence revient après connexion.
  // Tant qu’un token existe, ne pas retirer le marqueur (#1096) : fenêtre sans user mais session
  // en cours (refresh profil) ou portails MUI / Places hors conteneur restent blancs.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        document.documentElement.removeAttribute('data-dev-night-mode');
        document.documentElement.removeAttribute('data-night-palette');
      }
    } catch {
      /* ignore */
    }
  }, [user, isLoading]);

  // Dès que onboarding_completed_at est connu (depuis get-profile ou login),
  // on l'ancre en localStorage pour que la Sidebar ne flashe plus jamais,
  // même sans API call, même sur un autre onglet.
  useEffect(() => {
    if (isValidOnboardingTimestamp(user?.onboarding_completed_at) && user?.pro_id) {
      try {
        const key = `onboarding:v1:user:${user.pro_id}:startup_dismissed`;
        localStorage.setItem(key, '1');
      } catch { /* */ }
    }
  }, [user?.onboarding_completed_at, user?.pro_id]);

  // App native : rafraîchir le profil au retour au premier plan (JWT + UI à jour)
  useEffect(() => {
    if (!configService.isNative()) return undefined;
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('token')) return;
      refreshUser().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshUser]);

  useEffect(() => {
    const onSessionInvalid = () => {
      logout();
      if (shouldRedirectToLoginOnSessionExpiry()) {
        redirectToLogin();
      }
    };
    window.addEventListener(SESSION_INVALID_EVENT, onSessionInvalid);
    return () => window.removeEventListener(SESSION_INVALID_EVENT, onSessionInvalid);
  }, [logout]);

  // Autre onglet a vidé le JWT : aligner cet onglet (évite « Token manquant » + listes fantômes).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'token') return;
      if (event.newValue) return;
      logout();
      if (shouldRedirectToLoginOnSessionExpiry()) {
        redirectToLogin();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [logout]);

  const authValue = useMemo(() => ({
    user,
    login,
    logout,
    refreshUser,
    isAuthenticated,
    isLoading,
    isBetaTester,
  }), [user, login, logout, refreshUser, isAuthenticated, isLoading, isBetaTester]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
